/**
 * v1.4 RBAC 阶段 4 P0：前端权限点 Context + Hook
 *
 * 数据源：useAuthStore.user.permissions（由后端 /auth/me + login 注入）
 * 后端数据 = PermissionsCacheService.getOrLoad(userId)，与 PermissionsGuard 判断**完全同源**
 *
 * 用法：
 *   const can = useHasPermission('customer:edit');
 *   {can && <Button>编辑</Button>}
 *
 *   // 多权限点 AND（全部必须有）
 *   const canFull = useHasPermission(['customer:edit', 'customer:export']);
 *
 *   // 任一权限点 OR
 *   const canAny = useHasAnyPermission(['customer:edit', 'customer:transfer']);
 *
 * 安全网：permissions 未加载（undefined）时所有 hasPermission 调用返 false
 * → UI 默认隐藏受保护元素，避免渲染闪烁 / 越权显示
 */
import { createContext, useContext, useMemo } from 'react';
import { useAuthStore } from '../store/auth';

interface PermissionContextValue {
  permissions: Set<string>;
  loaded: boolean;
  has: (code: string | string[]) => boolean;
  hasAny: (codes: string[]) => boolean;
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: new Set(),
  loaded: false,
  has: () => false,
  hasAny: () => false,
});

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);

  const value = useMemo<PermissionContextValue>(() => {
    const list = user?.permissions;
    const loaded = Array.isArray(list);
    const set = new Set(loaded ? list! : []);
    const has = (code: string | string[]): boolean => {
      if (!loaded) return false; // 安全网：未加载时一律 false
      if (Array.isArray(code)) return code.every((c) => set.has(c));
      return set.has(code);
    };
    const hasAny = (codes: string[]): boolean => {
      if (!loaded) return false;
      return codes.some((c) => set.has(c));
    };
    return { permissions: set, loaded, has, hasAny };
  }, [user?.permissions]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

/** 检查是否拥有某权限点（或权限点数组的全部，AND 语义） */
export function useHasPermission(code: string | string[]): boolean {
  const ctx = useContext(PermissionContext);
  return ctx.has(code);
}

/** 检查是否拥有任一权限点（OR 语义） */
export function useHasAnyPermission(codes: string[]): boolean {
  const ctx = useContext(PermissionContext);
  return ctx.hasAny(codes);
}

/** 直接拿权限点 Set（少用，主要给 debug / 列表渲染场景） */
export function usePermissions() {
  return useContext(PermissionContext);
}
